import { createContext, useContext, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workoutsApi } from '@/lib/api';
import type { Workout } from '@/types';

interface ActiveWorkoutContextType {
  activeWorkout: Workout | null;
  isLoading: boolean;
  refetch: () => void;
  clearActiveWorkout: () => void;
}

const ActiveWorkoutContext = createContext<ActiveWorkoutContextType | null>(null);

export function ActiveWorkoutProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['activeWorkout'],
    queryFn: async () => {
      const res = await workoutsApi.active();
      return res.success ? res.data?.workout || null : null;
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refetch every minute
    refetchOnWindowFocus: true,
  });

  const clearActiveWorkout = useCallback(() => {
    queryClient.setQueryData(['activeWorkout'], null);
  }, [queryClient]);

  return (
    <ActiveWorkoutContext.Provider value={{
      activeWorkout: data || null,
      isLoading,
      refetch,
      clearActiveWorkout,
    }}>
      {children}
    </ActiveWorkoutContext.Provider>
  );
}

export function useActiveWorkout() {
  const context = useContext(ActiveWorkoutContext);
  if (!context) {
    throw new Error('useActiveWorkout must be used within an ActiveWorkoutProvider');
  }
  return context;
}
